import { uuidv4 } from '@firebase/util';
import { Injectable, OnDestroy } from '@angular/core';
import { QuotationService } from '../quotations/quotation.service';
import { LocalStorageService } from '../localstorage.service';
import { InvoicesService } from '../invoices/invoices.service';
import { UtilityService } from '../utility_functions/utility_functions.service';
import { DataCollectorService } from '../data_collector_services/data_collector_service.service';
import { ActivatedRoute } from '@angular/router';
import { JobsService } from '../jobs/jobs.service';
import { CustomersService } from '../customers/customers.service';
import { ProjectsService } from '../project/projects.service';
import { OrganizationService } from '../organization/organization.service';
import { ProductService } from '../products/products.service';
import { PaymentsService } from '../payments/payments.service';
import { ContractsService } from '../contracts/contracts.service';
import { AssetsService } from '../assets/assets.service';
import { RequestService } from '../request/request.service';
import { PropertyService } from '../properties/properties.service';
import { groupBy } from 'lodash';
import { Subject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class ZuperSdkDataService implements OnDestroy {

    childWindow: Window | null;

    getObjectURL = {
        'invoice': (id: string): string => this._invoicesService.getInvoiceUrl(id),
        'estimate': (id: string): string => this._quotationService.getQuotationUrl(id),
        'job': (id: string): string => this._jobsService.getJobUrl(id),
        'customer': (id: string): string => this._customerService.getCustomerUrl(id),
        'project': (id: string): string => this._projectService.projectsDetailsUrl(id),
        'organization': (id: string): string => this._organizationService.getOrganizationDetails(id),
        'product': (id: string): string => this._productService.getProductDetails(id),
        'payment': (id: string): string => this._paymentsService.getTransactionDetailUrl(id),
        'contract': (id: string): string => this._contractsService.getContractDetails(id),
        'asset': (id: string): string => this._assetsService.getAssetUrl(id),
        'request': (id: string): string => this._requestService.getRequestDetailsUrl(id),
        'property': (id: string): string => this._propertyService.getPropertyDetails(id),
    };

    currentObjectSubjectBasedOnModules = {
        'estimate': this._quotationService.currentQuotationDetails,
        'invoice': this._invoicesService.currentInvoiceDetails,
        'job': this._jobsService.currentJobDetails,
        'customer': this._customerService.currentCustomerDetails,
        'project': this._projectService.currentProjectDetails,
        'organization': this._organizationService.currentOrganizationDetails,
        'product': this._productService.currentProductDetails,
        'contract': this._contractsService.currentContractDetails,
        'asset': this._assetsService.currentAssetDetails,
        'request': this._requestService.currentRequestDetails,
        'property': this._propertyService.currentPropertyDetails,
    };

    fetchCurrentFormValue = new Subject();


    constructor(
        private _activatedRoute: ActivatedRoute,
        private _dataCollectorService: DataCollectorService,
        private _quotationService: QuotationService,
        private _localStorage: LocalStorageService,
        private _invoicesService: InvoicesService,
        private _jobsService: JobsService,
        private _customerService: CustomersService,
        private _projectService: ProjectsService,
        private _utilityService: UtilityService,
        private _productService: ProductService,
        private _paymentsService: PaymentsService,
        private _contractsService: ContractsService,
        private _assetsService: AssetsService,
        private _requestService: RequestService,
        private _organizationService: OrganizationService,
        private _propertyService: PropertyService
    ) {
    }

    get(data: any, childWindow: Window): void {
        this.childWindow = childWindow;
        const param = data.params[0];
        let key = '';
        let objectId = null;

        if (typeof param === 'object') {
            key = param['key'];
            objectId = param['uid'];
        } else {
            key = param;
        }
        let object = this._localStorage.getLocalStorage(key);
        if(object){
            this.sendDetailMessage(key, data, object)
            return;
        }
        const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot, true);
        const isDetailsPage = routeData['page'] === `${key}_details`;
        const isEditPage = routeData['page'] === `${key}_update`;
        if (isDetailsPage || isEditPage) {
            const sub$ = this.currentObjectSubjectBasedOnModules[key];
            const details = sub$.value;
            // Check if details are available
            const isDetailsAvailable = details && details[`${key}_uid`];
            const isMatchingObjectId = objectId ? isDetailsAvailable && details[`${key}_uid`] === objectId : false;
            if (isDetailsAvailable && (isMatchingObjectId || this.currentObjectSubjectBasedOnModules[key])) {
                if (isMatchingObjectId) {
                    this.sendDetailMessage(key, data, details);
                } else {
                    this.fetchDataFromAPI(data, key, routeData[`${key}_uid`]);
                }
            } else {
                this.fetchDataFromAPI(data, key, routeData[`${key}_uid`]);
            }
        } else if (key === `${routeData['page']}.form`) {
            this.fetchCurrentFormValue.next({ key, data });
        } else if (objectId) {
            this.fetchDataFromAPI(data, key, objectId);
        } else {
            this.sendNoDetailMessage(key, data);
        }
    }

    fetchDataFromAPI(data: any, key: string, id: string): void {
        if (key in this.getObjectURL) {
            const url = this.getObjectURL[key](id);
            this._dataCollectorService.getData(url).subscribe(
                (details: any) => {
                    if (details) { this.sendDetailMessage(key, data, details.data); }
                }, (error: any) => { this.sendNoDetailMessage(key, data); });
        } else {
            this.sendNoDetailMessage(key, data);
        }
    }

    sendNewFormValue(key: string, data: any, formValue: any): void {
        if (formValue) {
            this.sendDetailMessage(key, data, formValue);
        }
    }


    sendNoDetailMessage(key: string, data: any,): void {
        const uuid = uuidv4();
        this.sendMessage(this.childWindow, {
            id: data.id,
            instanceGuid: data.instanceGuid,
            result: {
                uid: uuid,
                response: `No ${key} data found`,
                success: false
            }
        });
    }

    sendDetailMessage(key: string, data: any, details: any): void {
        const uuid = uuidv4();
        this.sendMessage(this.childWindow, {
            id: data.id,
            instanceGuid: data.instanceGuid,
            result: {
                uid: uuid,
                response: details,
                success: true
            }
        });
    }

    sendMessage(childWindow: Window | null, message: any): void {
        childWindow = childWindow || this.childWindow;
        childWindow?.postMessage(
            message,
            window.origin
        );
    }

    serializerUserPermissions(permissions: any[]): any {
        return groupBy(permissions.map(i => ({
            module: i.permissions.module.module_key,
            page: i.permissions.permission_key.replace('.', '_')
        })), 'module');
    }

    ngOnDestroy(): void {
    }
}
